import { FC, Fragment, useState, useEffect, useRef, useCallback } from 'react';
import {
  ArrowLeftIcon,
  ChevronDarkRightIcon,
  Container,
  DashboardLayout,
  MultiStepProgress,
  StepWrapper,
} from 'Components';
import { useCardName, useCardType, useLocalization } from 'Hooks';
import { Box, Typography, Button, IconButton } from '@material-ui/core';
import { colors, gutters } from 'Theme/Variables';
import AddPhotoStep from './AddPhotoStep/AddPhotoStep';
import ProductTypeStep from './ProductTypeStep/ProductTypeStep';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { StaticContext } from 'react-router';
import { DeliveryStep } from './DeliveryStep';
import { PaymentStep } from './PaymentStep';
import { OrganisationStep } from './OrganisationStep';
import { useDispatch, useSelector } from 'react-redux';
import { ReportStep } from './ReportStep';
import { UserState, userStore } from 'Stores/User';
import { getCityListRequest, getTownOtherListRequest, onGetCustomerInfoRequest } from 'Stores/Account';
import { onOnlineApplicationRequest, storeCardTransactions } from 'Stores/CardTransactions';
import { applicationStore, decStep, getProductPriceRequest, getSubProductList, setStep } from 'Stores/CardApplication';
import { setActiveSelectedCreditCard } from 'Stores/CreditCardTransactions';
import CardApplicationErrors from 'Views/CardApplication/CartApplicationErrors';
import { CardProductCode } from 'Models/OnOnlineApplicationCardQueryModel';
import { normalizeHtmlContent } from 'Utils/HtmlContentNormalizer';
import plusOrcPng from 'Assets/Images/cardTypes/plusOrc.png';
import plusStdPng from 'Assets/Images/cardTypes/plusStd.png';
import plusOrcSvg from 'Assets/Images/cardTypes/plusOrc.svg';
import plusStdSvg from 'Assets/Images/cardTypes/plusStd.svg';
import iconChecked from 'Assets/Icons/icon-checked.svg';
// @ts-ignore
import './CardApplicationSteps.scss';
import { routePath } from 'Navigator/routes';

type CardApplicationStepsProps = RouteComponentProps<{}, StaticContext>;

const CardApplicationSteps: FC<CardApplicationStepsProps> = ({ location: { state } }: any) => {
  const history = useHistory<any>();
  const dispatch = useDispatch();
  const userData: UserState = useSelector(userStore);
  const { HideOrganizationStep, CustodyCustomerNumber, HasCustodyRelation } = useSelector(storeCardTransactions);
  const { activeApplicationData, applicationCard, step, cardApplicationStatus, updateApplicationId } = useSelector(
    applicationStore
  );
  const customerNumber = userData?.customerNumber;
  const sessionToken = userData?.sessionToken;
  const isPlus = state?.selectedType === 'plus' || applicationCard?.ProductCode?.startsWith('IKP_');
  const [showInformative, setShowInformative] = useState(isPlus && !state?.isUpdate && !state?.fromAddCreditCard);
  const hasNavigatedToPaymentStep = useRef(false);

  const historyItemType = history.location.state?.item;
  const relationShipApply = history.location.state?.relationShipApply ?? false;
  const { cardColor, cardCode, gradient } = useCardType(applicationCard?.ProductCode || historyItemType?.ProductCode);
  const currentCard = applicationCard || historyItemType;
  const CardPrintType = currentCard?.CardPrintType;
  const productName = useCardName(currentCard?.ProductCode, currentCard?.SubProductCode, true);
  const {} = useLocalization();

  const shouldSkipPhotoStep =
    currentCard?.ProductCode === 'IKT_IKT' || 
    currentCard?.ProductCode === 'IKT_STD' || 
    currentCard?.ProductCode === 'IKP_IKR' ||
    currentCard?.ProductCode === 'IKP_STD';

  const isUpdateCard = !!activeApplicationData?.CustomerNumber;
  const {
    web_card_application_progressbar_1,
    web_card_application_progressbar_2,
    web_card_application_progressbar_3,
    web_pc_istanbulcard_information_btn_apply,
    web_pc_istanbulcard_informative_text,
    web_pc_istanbulcard_informative_student_text,
    web_card_application_success_title,
    web_card_application_success_desc,
    web_card_application_success_desc2,

    web_card_application_success_application_number,
    web_pc_istanbulcard_apply_page_success_btn_apply_card,
    web_btn_anasayfa,
  } = useLocalization();
  useEffect(() => {
    dispatch(
      onGetCustomerInfoRequest({
        CustomerNumber: customerNumber,
        SessionToken: sessionToken,
      })
    );

    if (applicationCard?.ProductCode) {
      dispatch(
        getProductPriceRequest({
          CustomerNumber: customerNumber,
          SessionToken: sessionToken,
          ProductCode: applicationCard?.ProductCode || '',
          SubProductCode: applicationCard?.SubProductCode || '',
          CustodyCustomerNumber: CustodyCustomerNumber ? CustodyCustomerNumber : undefined,
        })
      );
      dispatch(
        getSubProductList({
          CustomerNumber: customerNumber,
          SessionToken: sessionToken,
          ProductCode: applicationCard?.ProductCode || '',
        })
      );
    }

    dispatch(
      getTownOtherListRequest({
        CustomerNumber: customerNumber,
        SessionToken: sessionToken,
        CityId: String(activeApplicationData?.AddressDetail?.CityCode),
      })
    );
  }, [activeApplicationData?.AddressDetail]);
  useEffect(() => {
    // AddCreditCard'dan döndüğümüzde bu kontrolü atla
    if (
      !state?.fromAddCreditCard &&
      !activeApplicationData?.CustomerNumber &&
      !activeApplicationData?.ProductCode &&
      !activeApplicationData?.SubProductCode &&
      !activeApplicationData?.SessionToken &&
      step != 0
    ) {
      history.push(routePath.cardApplication);
    }
    dispatch(
      getCityListRequest({
        CustomerNumber: customerNumber,
        SessionToken: sessionToken,
      })
    );
    if (!isUpdateCard && history.location?.state?.status == true && history.location?.state?.custody == undefined) {
      dispatch(
        onOnlineApplicationRequest({
          request: {
            CustomerNumber: customerNumber,
            SessionToken: sessionToken,
            ProductCode: currentCard?.ProductCode,
            SubProductCode: history.location?.state.stepData?.SubProductCode || null,
          },
          product: currentCard,
        })
      );
    }
  }, []);

  const organizationStep = {
    children: <OrganisationStep key="0" />,
    showTypes: '3',
    title: 'Kurum Bilgileri',
  };

  const plusSteps = shouldSkipPhotoStep
    ? [
        { children: <DeliveryStep key="address" />, showTypes: '', title: 'Teslimat Bilgileri' },
        { children: <PaymentStep key="payment" />, showTypes: '', title: 'Ödeme İşlemi' },
      ]
    : [
        { children: <AddPhotoStep key="photo" />, showTypes: '', title: 'Fotoğraf Ekle' },
        { children: <DeliveryStep key="address" />, showTypes: '', title: 'Teslimat Bilgileri' },
        { children: <PaymentStep key="payment" />, showTypes: '', title: 'Ödeme İşlemi' },
      ];

  const data = isPlus
    ? plusSteps
    : [
        {
          children: <ProductTypeStep relationShipApply={relationShipApply} key="-1" />,
          showTypes: '0, 3',
          title: 'Ürün Tipi',
        },
        {
          children: <ReportStep key="123" />,
          showTypes: '4',
          title: 'Kullanıcı Bilgileri',
        },
        ...(!HideOrganizationStep ? [organizationStep] : []),
        {
          children: <AddPhotoStep key="1" />,
          showTypes: '0, 1, 3, 4, 5',
          title: web_card_application_progressbar_1,
        },
        {
          children: <DeliveryStep key="4" />,
          showTypes: '0, 1, 2, 3, 4, 5',
          title: web_card_application_progressbar_2,
        },
        {
          children: <PaymentStep key="5" />,
          showTypes: '0, 1, 2, 3, 4, 5',
          title: web_card_application_progressbar_3,
        },
      ];

  const createStepData = () => {
    if (isPlus) return data;
    const type = CardPrintType;
    const tempData = [];
    for (let index = 0; index < data.length; index++) {
      const element = data[index];
      const inner = element.showTypes && element.showTypes.includes(String(type));
      if (inner) {
        tempData.push(element);
      }
    }
    return tempData;
  };

  const createTitleData = () => {
    if (isPlus) return data.map((d) => d.title);
    const type = CardPrintType;
    const tempData = [];
    for (let index = 0; index < data.length; index++) {
      const element = data[index];
      const inner = element.showTypes && element.showTypes.includes(String(type));
      if (inner) {
        tempData.push(element.title);
      }
    }
    return tempData;
  };

  const liveData = createStepData();
  const liveTitles = createTitleData();

  const goBack = useCallback(() => {
    // PaymentStep'ten geri gelindiğinde seçili kartı temizle
    const currentStepData = createStepData()[step];
    if (currentStepData?.children?.key === '5' || currentStepData?.children?.key === 'payment') {
      dispatch(setActiveSelectedCreditCard(null));
    }

    if (isUpdateCard) {
      if (step === 1) {
        history.goBack();
        return;
      }

      if (
        (applicationCard?.ProductCode === CardProductCode.OGRENCI ||
          applicationCard?.ProductCode === CardProductCode.OGRETMEN) &&
        step === 2
      ) {
        history.goBack();
        return;
      }
    }
    if (step === 0) {
      history.goBack();
    } else {
      dispatch(decStep());
    }
  }, [step, isUpdateCard, applicationCard?.ProductCode, history, dispatch]);

  // fromAddCreditCard state'i geldiğinde Payment step'in doğru index'ine yönlendir
  // Bu useEffect, liveData tanımlandıktan sonra çalışacak şekilde yerleştirildi
  // Geri gitme butonunu etkilememesi için sadece fromAddCreditCard true olduğunda ve bir kez çalışır
  useEffect(() => {
    if (
      state?.fromAddCreditCard &&
      CardPrintType !== undefined &&
      !hasNavigatedToPaymentStep.current &&
      liveData.length > 0
    ) {
      const paymentStepIndex = liveData.findIndex(
        (item) => item.children?.key === '5' || item.children?.key === 'payment'
      );
      if (paymentStepIndex !== -1) {
        dispatch(setStep(paymentStepIndex));
        hasNavigatedToPaymentStep.current = true;
      }
    }
    // liveData ve createStepData her render'da yeniden oluşturulduğu için dependency listesine eklenmez
    // hasNavigatedToPaymentStep.current ref olduğu için dependency listesine eklenmez
  }, [state?.fromAddCreditCard, CardPrintType, applicationCard?.ProductCode, HideOrganizationStep, dispatch]);

  if (isPlus && cardApplicationStatus === 6) {
    return (
      <DashboardLayout marginTop={0} noContainer>
        <div id="cardApplicationSteps">
          <Container>
            <div className="cardApplicationSteps">
              <div className="cardApplicationSteps__success">
                <div className="cardApplicationSteps__success__card">
                  <div className="cardApplicationSteps__success__card__check">
                    <img src={iconChecked} alt="success" />
                  </div>
                  <img
                    src={
                      applicationCard?.ProductCode === 'IKP_ORC'
                        ? plusOrcSvg
                        : plusStdSvg
                    }
                    alt="card"
                  />
                </div>

                <Typography variant="h5" className="cardApplicationSteps__success__title">
                  {web_card_application_success_title}
                </Typography>

                <div className="cardApplicationSteps__success__number">
                  <Typography variant="body1" className="cardApplicationSteps__success__number__title">
                    {web_card_application_success_application_number}
                  </Typography>
                  <Typography
                    variant="h6"
                    className="cardApplicationSteps__success__number__value"
                    style={{ fontWeight: 'bold' }}
                  >
                    {updateApplicationId || '111111'}
                  </Typography>
                </div>
                <Typography variant="body1" className="cardApplicationSteps__success__desc">
                  {web_card_application_success_desc}
                </Typography>
                <Typography variant="body1" className="cardApplicationSteps__success__desc">
                  {web_card_application_success_desc2}
                </Typography>

                <div className="cardApplicationSteps__success__buttons">
                  <Button
                    variant="contained"
                    onClick={() => history.push(routePath.cardApplications)}
                    color="secondary"
                    style={{ width: '550px' }}
                  >
                    {web_pc_istanbulcard_apply_page_success_btn_apply_card}
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={() => history.push(routePath.home)}
                    color="secondary"
                    style={{ width: '550px' }}
                  >
                    {web_btn_anasayfa}
                  </Button>
                </div>
              </div>
            </div>
          </Container>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout marginTop={0} noContainer>
      <Fragment>
        <CardApplicationErrors />
        <div id="cardApplicationSteps">
          <div
            style={gradient ? { background: gradient } : { backgroundColor: cardCode ? cardCode : cardColor }}
            className="cardApplicationSteps__bg"
          />
          <Container>
            <IconButton aria-label="Geri dön" onClick={() => goBack()} className="cardApplicationSteps__back" size="small">
              <ArrowLeftIcon />
            </IconButton>

            <div className="cardApplicationSteps">
              <Typography variant="h3" component="h1" align="center">
                <Box
                  mt={showInformative || cardApplicationStatus === 6 ? '50px' : gutters.big}
                  mb={gutters.big}
                  color={colors.whiteGray}
                  fontWeight={600}
                >
                  {productName}
                </Box>
              </Typography>

              {showInformative ? (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="80vh">
                  <img
                    src={
                      applicationCard?.ProductCode === 'IKP_ORC'
                        ? plusOrcPng
                        : plusStdPng
                    }
                    alt="card"
                    style={{ width: '100px', marginBottom: '32px', marginTop: '50px' }}
                  />
                  <Typography
                    variant="body1"
                    align="left"
                    dangerouslySetInnerHTML={{
                      __html: normalizeHtmlContent(
                        applicationCard?.ProductCode === 'IKP_ORC'
                          ? web_pc_istanbulcard_informative_student_text
                          : web_pc_istanbulcard_informative_text
                      ),
                    }}
                  />

                  <Button
                    onClick={() => setShowInformative(false)}
                    variant="contained"
                    color="secondary"
                    style={{ width: '550px' }}
                  >
                    {web_pc_istanbulcard_information_btn_apply}
                  </Button>
                </Box>
              ) : (
                <StepWrapper noStyle>
                  <div className="cardApplicationSteps__stepper">
                    <MultiStepProgress titles={liveTitles} activeStep={step} />
                  </div>
                  {liveData.map((item, index) => step === index && item.children)}
                </StepWrapper>
              )}
            </div>
          </Container>
        </div>
      </Fragment>
    </DashboardLayout>
  );
};

export default CardApplicationSteps;
